import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaUser,
  FaPhone,
  FaEdit,
  FaPlus,
  FaExternalLinkAlt,
  FaChevronDown,
  FaCheck,
  FaTrash,
} from "react-icons/fa";

import CommonModal from "Components/CommonModal";
import Modal from "Components/UI/Modal";
import Button from "Components/UI/Button";
import RichTextEditor from "Components/UI/RichTextEditor";
import { commonModalSetting } from "JS/common";
import {
  getWorkMemoDetail,
  addWorkMemoEntry,
  updateWorkMemoEntry,
  updateWorkMemoStatus,
  deleteWorkMemoEntry,
} from "JS/API";

import { commonStyles, designTokens, badgeStyles, listPageStyles } from "utils/designSystem";
import { renderBold } from "utils/markdown";
import { detailModalStyles } from "utils/modalDesignSystem";

// 채널 옵션
const CHANNEL_OPTIONS = [
  { value: 'markpick', label: '마크픽' },
  { value: 'kmong', label: '크몽' },
  { value: 'phone', label: '전화' },
  { value: 'email', label: '메일' },
  { value: 'kakao', label: '카톡' },
  { value: 'kakao_channel', label: '카톡채널' },
  { value: 'ceo_order', label: '대표님 지시' },
  { value: 'etc', label: '기타' },
];

// 채널 한글명
const getChannelLabel = (channel) => {
  const labels = {
    markpick: '마크픽',
    kmong: '크몽',
    phone: '전화',
    email: '메일',
    kakao: '카톡',
    kakao_channel: '카톡채널',
    ceo_order: '대표님 지시',
    etc: '기타',
  };
  return labels[channel] || channel;
};

// 상태 한글명
const getStatusLabel = (status) => {
  const labels = {
    in_progress: '진행중',
    completed: '완료',
    on_hold: '보류',
  };
  return labels[status] || status;
};

// 채널별 배지 색상
const getChannelBadgeStyle = (channel) => {
  const styles = {
    markpick: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    kmong: { backgroundColor: '#fef3c7', color: '#d97706' },
    phone: { backgroundColor: '#dcfce7', color: '#16a34a' },
    email: { backgroundColor: '#f3e8ff', color: '#9333ea' },
    kakao: { backgroundColor: '#fef9c3', color: '#ca8a04' },
    kakao_channel: { backgroundColor: '#fde68a', color: '#92400e' },
    ceo_order: { backgroundColor: '#fee2e2', color: '#dc2626' },
    etc: { backgroundColor: '#f1f5f9', color: '#475569' },
  };
  return styles[channel] || { backgroundColor: '#f1f5f9', color: '#475569' };
};

// 상태별 배지 색상
const getStatusBadgeStyle = (status) => {
  const styles = {
    in_progress: { backgroundColor: '#dbeafe', color: '#1d4ed8' },
    completed: { backgroundColor: '#dcfce7', color: '#16a34a' },
    on_hold: { backgroundColor: '#f1f5f9', color: '#475569' },
  };
  return styles[status] || styles.in_progress;
};

// 타임라인 아이콘
const getTimelineIcon = (type) => {
  const icons = {
    memo: { icon: '📝', color: '#3b82f6', label: '메모' },
    markpick_agent: { icon: '📋', color: '#059669', label: '출원대행' },
    markpick_application: { icon: '📄', color: '#8b5cf6', label: '출원정보' },
    markpick_status: { icon: '🔄', color: '#f59e0b', label: '상태변경' },
    markpick_comment: { icon: '💬', color: '#ec4899', label: '마크픽 댓글' },
    markpick_payment: { icon: '💳', color: '#06b6d4', label: '기본결제' },
    markpick_add_payment: { icon: '💰', color: '#f97316', label: '추가결제' },
  };
  return icons[type] || icons.memo;
};


const WorkMemoDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const entryId = searchParams.get('entry');

  const [memo, setMemo] = useState(null);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [loading, setLoading] = useState(true);

  // 현재 시간을 datetime-local 형식으로 반환
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 모달 상태
  const [addMemoModal, setAddMemoModal] = useState(false);
  const [editMemoModal, setEditMemoModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [newMemoContent, setNewMemoContent] = useState('');
  const [newMemoAt, setNewMemoAt] = useState(getCurrentDateTime()); // 문의 시간
  const [newMemoChannel, setNewMemoChannel] = useState('phone'); // 채널 (기본값: 전화)

  // 상태 드롭다운
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });

  // 스타일
  const styles = {
    container: listPageStyles.container,
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      marginBottom: designTokens.spacing.xl,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      cursor: 'pointer',
      fontSize: '14px',
      color: designTokens.colors.text.secondary,
      transition: 'all 0.2s',
    },
    headerInfo: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.md,
    },
    customerName: {
      fontSize: '22px',
      fontWeight: '700',
      color: designTokens.colors.text.primary,
    },
    headerMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      fontSize: '14px',
      color: designTokens.colors.text.secondary,
    },
    // 현재 메모 (클릭한 entry)
    currentMemoCard: {
      ...commonStyles.card,
      marginBottom: designTokens.spacing.lg,
      border: `2px solid ${designTokens.colors.primary}`,
      backgroundColor: '#f8fafc',
    },
    currentMemoHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: designTokens.spacing.md,
    },
    currentMemoLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: designTokens.colors.primary,
      backgroundColor: '#dbeafe',
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
      borderRadius: designTokens.borderRadius.sm,
      marginBottom: designTokens.spacing.sm,
    },
    currentMemoContent: {
      fontSize: '16px',
      lineHeight: 1.6,
      color: designTokens.colors.text.primary,
      whiteSpace: 'pre-wrap',
    },
    currentMemoMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.lg,
      marginTop: designTokens.spacing.md,
      paddingTop: designTokens.spacing.md,
      borderTop: `1px solid ${designTokens.colors.borderLight}`,
      fontSize: '13px',
      color: designTokens.colors.text.muted,
    },
    // 마크픽 연결 정보
    markpickCard: {
      ...commonStyles.card,
      marginBottom: designTokens.spacing.lg,
      backgroundColor: '#fafafa',
    },
    markpickHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      marginBottom: designTokens.spacing.md,
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.secondary,
    },
    markpickGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: designTokens.spacing.md,
    },
    markpickSection: {
      backgroundColor: designTokens.colors.surface,
      padding: designTokens.spacing.md,
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
    },
    markpickSectionTitle: {
      fontSize: '12px',
      fontWeight: '600',
      color: designTokens.colors.text.tertiary,
      marginBottom: designTokens.spacing.sm,
    },
    markpickItem: {
      fontSize: '13px',
      color: designTokens.colors.text.primary,
      padding: `${designTokens.spacing.xs} 0`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    markpickLink: {
      fontSize: '12px',
      color: designTokens.colors.primary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    // 전체 이력
    historyCard: {
      ...commonStyles.card,
    },
    historyHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: designTokens.spacing.lg,
      paddingBottom: designTokens.spacing.md,
      borderBottom: `1px solid ${designTokens.colors.border}`,
    },
    historyTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: designTokens.colors.text.primary,
    },
    historyList: {
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.sm,
    },
    historyItem: {
      display: 'flex',
      gap: designTokens.spacing.md,
      padding: designTokens.spacing.md,
      backgroundColor: '#fafafa',
      borderRadius: designTokens.borderRadius.md,
      border: `1px solid ${designTokens.colors.borderLight}`,
      transition: 'all 0.2s',
    },
    historyItemCurrent: {
      backgroundColor: '#eff6ff',
      borderColor: designTokens.colors.primary,
    },
    historyIcon: {
      fontSize: '18px',
      width: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      flexShrink: 0,
    },
    historyContent: {
      flex: 1,
      minWidth: 0,
    },
    historyContentHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: designTokens.spacing.xs,
    },
    historyDate: {
      fontSize: '12px',
      color: designTokens.colors.text.muted,
    },
    historyType: {
      fontSize: '11px',
      fontWeight: '500',
      padding: `2px ${designTokens.spacing.sm}`,
      borderRadius: designTokens.borderRadius.sm,
      backgroundColor: '#e2e8f0',
      color: designTokens.colors.text.tertiary,
    },
    historyText: {
      fontSize: '14px',
      color: designTokens.colors.text.primary,
      lineHeight: 1.4,
    },
    historyActions: {
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      marginTop: designTokens.spacing.xs,
    },
    actionLink: {
      fontSize: '12px',
      color: designTokens.colors.primary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    // 모달
    modalContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: designTokens.spacing.lg,
    },
    formField: detailModalStyles.formField,
    formLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: designTokens.colors.text.secondary,
    },
    textarea: {
      width: '100%',
      minHeight: '120px',
      padding: designTokens.spacing.md,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px',
      resize: 'vertical',
      fontFamily: 'inherit',
    },
    datetimeInput: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      fontSize: '14px',
      outline: 'none',
      cursor: 'pointer',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: designTokens.spacing.md,
      marginTop: designTokens.spacing.md,
    },
    channelButtonGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: designTokens.spacing.xs,
    },
    channelButton: {
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
      borderRadius: designTokens.borderRadius.sm,
      border: `1px solid ${designTokens.colors.border}`,
      backgroundColor: designTokens.colors.surface,
      color: designTokens.colors.text.secondary,
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    channelButtonActive: {
      padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`,
      borderRadius: designTokens.borderRadius.sm,
      border: `1px solid ${designTokens.colors.primary}`,
      backgroundColor: designTokens.colors.primary,
      color: designTokens.colors.surface,
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    // 상태 드롭다운
    statusDropdownContainer: {
      position: 'relative',
      display: 'inline-block',
    },
    statusBadgeClickable: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      transition: 'opacity 0.2s',
    },
    statusDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      marginTop: '4px',
      backgroundColor: designTokens.colors.surface,
      border: `1px solid ${designTokens.colors.border}`,
      borderRadius: designTokens.borderRadius.md,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 100,
      minWidth: '120px',
      overflow: 'hidden',
    },
    statusOption: {
      padding: `${designTokens.spacing.sm} ${designTokens.spacing.md}`,
      fontSize: '14px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: designTokens.spacing.sm,
      transition: 'background-color 0.2s',
    },
  };

  // 메모 상세 조회
  const getMemoDetail = async () => {
    setLoading(true);

    const result = await getWorkMemoDetail(id);

    // 에러 처리
    if (typeof result === 'string') {
      setLoading(false);
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      if (result === 'notFound') {
        commonModalSetting(setAlertBox, true, "alert", "메모를 찾을 수 없습니다.");
        navigate('/work-memo');
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "상세 정보를 불러오는데 실패했습니다.");
      return;
    }

    // API 응답 처리
    const memoData = result?.data?.data || result?.data;
    setMemo(memoData);

    // entryId가 있으면 해당 entry 찾기
    if (entryId && memoData?.entries) {
      const entry = memoData.entries.find(e => e.entry_id === parseInt(entryId));
      setCurrentEntry(entry || memoData.entries[0]);
    } else if (memoData?.entries?.length > 0) {
      setCurrentEntry(memoData.entries[0]); // 기본: 최신 entry
    }

    setLoading(false);
  };

  // 날짜 포맷
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${year}.${month}.${day < 10 ? '0' + day : day} ${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
  };

  // 간단 날짜 포맷
  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${month}.${day < 10 ? '0' + day : day} ${hours < 10 ? '0' + hours : hours}:${minutes < 10 ? '0' + minutes : minutes}`;
  };

  // 결제 상태 포맷
  const formatPaidStatus = (status) => {
    const labels = {
      ready: '결제 대기',
      paid: '결제 완료',
      failed: '결제 실패',
      canceled: '결제 취소',
      refund: '환불 완료',
    };
    return labels[status] || status;
  };

  // 로컬 시간(KST) 그대로 전송 (UTC 변환 없이)
  const formatLocalDateTime = (dateStr) => {
    if (!dateStr) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }
    // datetime-local은 "2024-12-31T14:30" 형식, 초 추가
    return `${dateStr}:00`;
  };

  // 메모 추가 핸들러
  const handleAddMemo = async () => {
    if (!newMemoContent.trim()) {
      commonModalSetting(setAlertBox, true, "alert", "메모 내용을 입력해주세요.");
      return;
    }

    const result = await addWorkMemoEntry(id, {
      channel: newMemoChannel,
      content: newMemoContent,
      memo_at: formatLocalDateTime(newMemoAt),
    });

    // 에러 처리
    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "메모 추가에 실패했습니다.");
      return;
    }

    setNewMemoContent('');
    setNewMemoAt(getCurrentDateTime());
    setAddMemoModal(false);
    commonModalSetting(setAlertBox, true, "alert", "메모가 추가되었습니다.");

    // 상세 정보 새로고침
    getMemoDetail();
  };

  // 메모 수정 핸들러
  const handleEditMemo = async () => {
    if (!newMemoContent.trim()) {
      commonModalSetting(setAlertBox, true, "alert", "메모 내용을 입력해주세요.");
      return;
    }

    const result = await updateWorkMemoEntry(id, editingEntry.entry_id, {
      channel: newMemoChannel,
      content: newMemoContent,
      memo_at: formatLocalDateTime(newMemoAt),
    });

    // 에러 처리
    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "메모 수정에 실패했습니다.");
      return;
    }

    setNewMemoContent('');
    setNewMemoAt(getCurrentDateTime());
    setEditingEntry(null);
    setEditMemoModal(false);
    commonModalSetting(setAlertBox, true, "alert", "메모가 수정되었습니다.");

    // 상세 정보 새로고침
    getMemoDetail();
  };

  // datetime-local 형식으로 변환
  const toDateTimeLocal = (isoString) => {
    if (!isoString) return getCurrentDateTime();
    const date = new Date(isoString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // 수정 모달 열기
  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setNewMemoContent(entry.content);
    setNewMemoAt(toDateTimeLocal(entry.memo_at || entry.created_at));
    setNewMemoChannel(entry.channel || 'phone');
    setEditMemoModal(true);
  };

  // 삭제 확인 모달 열기
  const openDeleteModal = (entry) => {
    setDeletingEntry(entry);
    setDeleteConfirmModal(true);
  };

  // Entry 삭제 핸들러
  const handleDeleteEntry = async () => {
    if (!deletingEntry?.entry_id) return;

    const result = await deleteWorkMemoEntry(id, deletingEntry.entry_id);

    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "삭제에 실패했습니다.");
      return;
    }

    setDeleteConfirmModal(false);
    setDeletingEntry(null);
    commonModalSetting(setAlertBox, true, "alert", "메모가 삭제되었습니다.");

    // 상세 정보 새로고침
    getMemoDetail();
  };

  // 상태 변경 핸들러
  const handleStatusChange = async (newStatus) => {
    if (newStatus === memo.status) {
      setStatusDropdownOpen(false);
      return;
    }

    const newStatusLabel = getStatusLabel(newStatus);

    const result = await updateWorkMemoStatus(id, newStatus);

    // 에러 처리
    if (typeof result === 'string') {
      if (result === 'tokenExpired' || result === 'duplicateLogin') {
        return;
      }
      commonModalSetting(setAlertBox, true, "alert", "상태 변경에 실패했습니다.");
      return;
    }

    setMemo(prev => ({
      ...prev,
      status: newStatus,
    }));

    setStatusDropdownOpen(false);
    commonModalSetting(setAlertBox, true, "alert", `상태가 "${newStatusLabel}"(으)로 변경되었습니다.`);
  };

  // 다른 이력 항목 클릭
  const handleHistoryItemClick = (item) => {
    if (item.type === 'memo' && item.entry_id) {
      const entry = memo.entries.find(e => e.entry_id === item.entry_id);
      if (entry) {
        setCurrentEntry(entry);
        // URL도 업데이트
        navigate(`/work-memo/${id}?entry=${item.entry_id}`, { replace: true });
      }
    }
  };

  useEffect(() => {
    document.title = "마크픽 관리자 > 업무 메모 상세";
    getMemoDetail();
  }, [id, entryId]);

  if (loading || !memo) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '100px 0', color: designTokens.colors.text.tertiary }}>
          불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate('/work-memo')}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = designTokens.colors.surface}
        >
          <FaArrowLeft size={14} />
          목록
        </button>

        <div style={styles.headerInfo}>
          <span style={styles.customerName}>{memo.customer_name}</span>
          <span style={{
            ...badgeStyles.base,
            ...getChannelBadgeStyle(memo.entries?.[0]?.channel),
          }}>
            {getChannelLabel(memo.entries?.[0]?.channel)}
          </span>

          {/* 상태 드롭다운 */}
          <div style={styles.statusDropdownContainer}>
            <span
              style={{
                ...badgeStyles.base,
                ...getStatusBadgeStyle(memo.status),
                ...styles.statusBadgeClickable,
              }}
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            >
              {getStatusLabel(memo.status)}
              <FaChevronDown size={10} style={{
                transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                transition: 'transform 0.2s'
              }} />
            </span>

            {statusDropdownOpen && (
              <>
                {/* 배경 클릭 시 닫기 */}
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 99,
                  }}
                  onClick={() => setStatusDropdownOpen(false)}
                />
                <div style={styles.statusDropdown}>
                  {['in_progress', 'completed', 'on_hold'].map(status => (
                    <div
                      key={status}
                      style={{
                        ...styles.statusOption,
                        backgroundColor: memo.status === status ? '#f1f5f9' : 'transparent',
                      }}
                      onClick={() => handleStatusChange(status)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = memo.status === status ? '#f1f5f9' : 'transparent'}
                    >
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusBadgeStyle(status).color,
                      }} />
                      {getStatusLabel(status)}
                      {memo.status === status && (
                        <FaCheck size={12} style={{ marginLeft: 'auto', color: designTokens.colors.primary }} />
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div style={styles.headerMeta}>
          {memo.contact && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaPhone size={12} /> {memo.contact}
            </span>
          )}
        </div>
      </div>

      {/* 현재 선택된 메모 */}
      {currentEntry && (
        <div style={styles.currentMemoCard}>
          <div style={styles.currentMemoHeader}>
            <div>
              <div style={styles.currentMemoLabel}>선택한 메모</div>
              <div
                style={styles.currentMemoContent}
                dangerouslySetInnerHTML={{ __html: renderBold(currentEntry.content) }}
              />
            </div>
            <div style={{ display: 'flex', gap: designTokens.spacing.sm }}>
              <Button
                variant="secondary"
                icon={<FaEdit size={12} />}
                onClick={() => openEditModal(currentEntry)}
                style={{ padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`, fontSize: '12px' }}
              >
                수정
              </Button>
              <Button
                variant="danger"
                icon={<FaTrash size={12} />}
                onClick={() => openDeleteModal(currentEntry)}
                style={{ padding: `${designTokens.spacing.xs} ${designTokens.spacing.sm}`, fontSize: '12px' }}
              >
                삭제
              </Button>
            </div>
          </div>
          <div style={styles.currentMemoMeta}>
            <span>{formatDateTime(currentEntry.memo_at || currentEntry.created_at)}</span>
            <span>작성자: {currentEntry.admin_name || currentEntry.created_by}</span>
          </div>
        </div>
      )}

      {/* 마크픽 연결 정보 (있는 경우만) */}
      {memo.user_pk && (
        <div style={styles.markpickCard}>
          <div style={styles.markpickHeader}>
            <FaUser size={14} style={{ color: designTokens.colors.primary }} />
            연결된 마크픽 회원: {memo.user_info.user_name} ({memo.user_info.email})
          </div>

          <div style={styles.markpickGrid}>
            {/* 출원대행 */}
            {memo.agents?.length > 0 && (
              <div style={styles.markpickSection}>
                <div style={styles.markpickSectionTitle}>📋 출원대행 ({memo.agents.length}건)</div>
                {memo.agents.map(agent => (
                  <div key={agent.apply_agent_pk} style={styles.markpickItem}>
                    <span>{agent.mark_name} - {agent.progress_status_name}</span>
                    <span
                      style={styles.markpickLink}
                      onClick={() => navigate(`/agencies/${agent.apply_agent_pk}`)}
                    >
                      상세 <FaExternalLinkAlt size={10} />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 출원정보 */}
            {memo.applications?.length > 0 && (
              <div style={styles.markpickSection}>
                <div style={styles.markpickSectionTitle}>📄 출원정보 ({memo.applications.length}건)</div>
                {memo.applications.map(app => (
                  <div key={app.application_info_id} style={styles.markpickItem}>
                    <span>{app.class_no}류 ({app.application_number})</span>
                    <span
                      style={styles.markpickLink}
                      onClick={() => navigate(`/application-info/${app.application_info_id}`)}
                    >
                      상세 <FaExternalLinkAlt size={10} />
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* 추가결제 */}
            {memo.added_payments?.length > 0 && (
              <div style={styles.markpickSection}>
                <div style={styles.markpickSectionTitle}>💰 추가결제 ({memo.added_payments.length}건)</div>
                {memo.added_payments.map(payment => (
                  <div key={payment.add_payment_pk} style={styles.markpickItem}>
                    <span>{payment.paid_reason} {payment.merchant_price.toLocaleString()}원</span>
                    <span style={{ fontSize: '12px', color: designTokens.colors.text.muted }}>
                      {formatPaidStatus(payment.paid_status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 전체 이력 */}
      <div style={styles.historyCard}>
        <div style={styles.historyHeader}>
          <span style={styles.historyTitle}>전체 이력</span>
          <Button
            variant="primary"
            icon={<FaPlus />}
            onClick={() => {
              setNewMemoContent('');
              setNewMemoAt(getCurrentDateTime());
              setAddMemoModal(true);
            }}
          >
            메모 추가
          </Button>
        </div>

        <div style={styles.historyList}>
          {memo.timeline.map(item => {
            const iconInfo = getTimelineIcon(item.type);
            const isCurrent = item.type === 'memo' && item.entry_id === currentEntry?.entry_id;

            // 메모 타입은 사용자 지정 시간(memo_at)을, 그 외는 created_at 사용
            let displayDate = item.created_at;
            if (item.type === 'memo') {
              if (item.memo_at) {
                displayDate = item.memo_at;
              } else if (item.entry_id) {
                const matched = memo.entries?.find(e => e.entry_id === item.entry_id);
                if (matched?.memo_at) displayDate = matched.memo_at;
              }
            }

            return (
              <div
                key={item.id}
                style={{
                  ...styles.historyItem,
                  ...(isCurrent ? styles.historyItemCurrent : {}),
                  cursor: item.type === 'memo' ? 'pointer' : 'default',
                }}
                onClick={() => handleHistoryItemClick(item)}
              >
                <div style={{
                  ...styles.historyIcon,
                  borderColor: isCurrent ? designTokens.colors.primary : designTokens.colors.border,
                }}>
                  {iconInfo.icon}
                </div>
                <div style={styles.historyContent}>
                  <div style={styles.historyContentHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: designTokens.spacing.sm }}>
                      <span style={styles.historyDate}>{formatShortDate(displayDate)}</span>
                      <span style={{
                        ...styles.historyType,
                        backgroundColor: item.editable ? '#dbeafe' : '#e2e8f0',
                        color: item.editable ? '#1d4ed8' : designTokens.colors.text.tertiary,
                      }}>
                        {iconInfo.label}
                        {!item.editable && ' · 마크픽'}
                      </span>
                      {item.type === 'memo' && item.channel && (
                        <span style={{
                          ...badgeStyles.base,
                          ...getChannelBadgeStyle(item.channel),
                          fontSize: '11px',
                          padding: `2px ${designTokens.spacing.sm}`,
                        }}>
                          {getChannelLabel(item.channel)}
                        </span>
                      )}
                      {isCurrent && (
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: designTokens.colors.primary,
                          color: '#fff',
                          padding: `2px ${designTokens.spacing.sm}`,
                          borderRadius: designTokens.borderRadius.sm,
                        }}>
                          현재 보는 중
                        </span>
                      )}
                    </div>
                    {item.editable && !isCurrent && (
                      <div style={{ display: 'flex', gap: designTokens.spacing.sm }}>
                        <span
                          style={styles.actionLink}
                          onClick={(e) => {
                            e.stopPropagation();
                            const entry = memo.entries.find(e => e.entry_id === item.entry_id);
                            if (entry) openEditModal(entry);
                          }}
                        >
                          <FaEdit size={11} /> 수정
                        </span>
                        <span
                          style={{ ...styles.actionLink, color: designTokens.colors.danger }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const entry = memo.entries.find(e => e.entry_id === item.entry_id);
                            if (entry) openDeleteModal(entry);
                          }}
                        >
                          <FaTrash size={11} /> 삭제
                        </span>
                      </div>
                    )}
                  </div>
                  <div
                    style={styles.historyText}
                    dangerouslySetInnerHTML={{ __html: renderBold(item.content) }}
                  />
                  {item.link && (
                    <div style={styles.historyActions}>
                      <span
                        style={styles.actionLink}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(item.link);
                        }}
                      >
                        바로가기 <FaExternalLinkAlt size={10} />
                      </span>
                    </div>
                  )}
                  {(item.admin_name || item.created_by) && (
                    <div style={{ fontSize: '11px', color: designTokens.colors.text.muted, marginTop: '4px' }}>
                      {item.admin_name || item.created_by}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 메모 추가 모달 */}
      {addMemoModal && (
        <Modal
          title="메모 추가"
          onClose={() => setAddMemoModal(false)}
          width="500px"
        >
          <div style={styles.modalContent}>
            <div style={styles.formField}>
              <label style={styles.formLabel}>채널 *</label>
              <div style={styles.channelButtonGroup}>
                {CHANNEL_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewMemoChannel(opt.value)}
                    style={newMemoChannel === opt.value ? styles.channelButtonActive : styles.channelButton}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>
                문의 시간
                <span style={{ fontSize: '12px', color: designTokens.colors.text.muted, marginLeft: '8px', fontWeight: 'normal' }}>
                  (기본: 현재 시간)
                </span>
              </label>
              <input
                type="datetime-local"
                style={styles.datetimeInput}
                value={newMemoAt}
                onChange={(e) => setNewMemoAt(e.target.value)}
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>
                내용 *
                <span style={{ fontSize: '11px', color: designTokens.colors.text.muted, marginLeft: '8px', fontWeight: 'normal' }}>
                  Ctrl+B: 굵게
                </span>
              </label>
              <RichTextEditor
                value={newMemoContent}
                onChange={setNewMemoContent}
                placeholder="진행 내용을 입력하세요..."
                minHeight="120px"
              />
            </div>
            <div style={styles.modalActions}>
              <Button variant="secondary" onClick={() => setAddMemoModal(false)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleAddMemo}>
                저장
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* 메모 수정 모달 */}
      {editMemoModal && (
        <Modal
          title="메모 수정"
          onClose={() => setEditMemoModal(false)}
          width="500px"
        >
          <div style={styles.modalContent}>
            <div style={styles.formField}>
              <label style={styles.formLabel}>채널</label>
              <div style={styles.channelButtonGroup}>
                {CHANNEL_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewMemoChannel(opt.value)}
                    style={newMemoChannel === opt.value ? styles.channelButtonActive : styles.channelButton}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>문의 시간</label>
              <input
                type="datetime-local"
                style={styles.datetimeInput}
                value={newMemoAt}
                onChange={(e) => setNewMemoAt(e.target.value)}
              />
            </div>
            <div style={styles.formField}>
              <label style={styles.formLabel}>
                내용 *
                <span style={{ fontSize: '11px', color: designTokens.colors.text.muted, marginLeft: '8px', fontWeight: 'normal' }}>
                  Ctrl+B: 굵게
                </span>
              </label>
              <RichTextEditor
                value={newMemoContent}
                onChange={setNewMemoContent}
                placeholder="진행 내용을 입력하세요..."
                minHeight="120px"
              />
            </div>
            <div style={styles.modalActions}>
              <Button variant="secondary" onClick={() => setEditMemoModal(false)}>
                취소
              </Button>
              <Button variant="primary" onClick={handleEditMemo}>
                저장
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* 삭제 확인 모달 */}
      {deleteConfirmModal && (
        <Modal
          title="메모 삭제"
          onClose={() => setDeleteConfirmModal(false)}
          width="400px"
        >
          <div style={{ textAlign: 'center', padding: designTokens.spacing.lg }}>
            <FaTrash size={32} style={{ color: designTokens.colors.danger, marginBottom: designTokens.spacing.md }} />
            <p style={{ fontSize: '16px', marginBottom: designTokens.spacing.sm }}>
              이 메모를 삭제하시겠습니까?
            </p>
            <p style={{ fontSize: '13px', color: designTokens.colors.text.muted }}>
              삭제된 메모는 복구할 수 없습니다.
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: designTokens.spacing.md, marginTop: designTokens.spacing.md }}>
            <Button variant="secondary" onClick={() => setDeleteConfirmModal(false)}>
              취소
            </Button>
            <Button variant="danger" onClick={handleDeleteEntry}>
              삭제
            </Button>
          </div>
        </Modal>
      )}

      {/* 알림 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {}}
        />
      )}
    </div>
  );
};

export default WorkMemoDetail;
